import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Divider,
  Chip,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Stack,
} from "@mui/material";
import {
  Check,
  Close,
  Schedule,
  CalendarToday,
  Edit,
  Person,
  Notifications,
} from "@mui/icons-material";
import { approveRejectTicket, getAllForApprovalTickets } from "../services/api";
import { convertToRawDate } from "../shared/base";
import { toast, ToastContainer } from "react-toastify";

const ApprovalWorkflow = () => {
  const [approvalDialog, setApprovalDialog] = useState({
    open: false,
    ticket: null,
    ActualStartDate: "",
    ActualEndDate: "",
    LeadTime: "",
    Comment: "",
  });
  const [notification, setNotification] = useState(null);
  const [forApprovalTickets, setForApprovalTickets] = useState([]);

  const _getAllForApprovalTickets = async () => {
    const getAllForApprovalApi = await getAllForApprovalTickets();
    if (getAllForApprovalApi && getAllForApprovalApi.success) {
      setForApprovalTickets(getAllForApprovalApi.data);
    }
  };

  useEffect(() => {
    _getAllForApprovalTickets();
  }, []);

  const handleApprove = (ticket) => {
    setApprovalDialog({
      open: true,
      ticket,
      ActualStartDate: ticket.ActualStartDate,
      ActualEndDate: ticket.ActualEndDate,
      LeadTime: ticket.LeadTime,
      Comment: "",
      IsApproved: true,
    });
  };

  const handleReject = (ticket) => {
    setApprovalDialog({
      open: true,
      ticket,
      ActualStartDate: ticket.ActualStartDate,
      ActualEndDate: ticket.ActualEndDate,
      LeadTime: ticket.LeadTime,
      Comment: "",
      IsApproved: false,
    });
  };

  const confirmApproval = async () => {
    const {
      ticket,
      IsApproved,
      ActualStartDate,
      ActualEndDate,
      LeadTime,
      Comment,
    } = approvalDialog;

    // Validate date order
    if (
      ActualStartDate &&
      ActualEndDate &&
      new Date(ActualStartDate) > new Date(ActualEndDate)
    ) {
      toast.error("Actual Start Date must be earlier than Actual End Date.");
      return;
    }

    const updatedTicket = {
      TicketID: ticket.ID,
      ActualStartDate,
      ActualEndDate,
      LeadTime,
      Comment,
      IsApproved,
    };

    const ticketDecision = await approveRejectTicket(updatedTicket);
    if (ticketDecision && ticketDecision.success) {
      _getAllForApprovalTickets();
      setApprovalDialog({ ...approvalDialog, open: false });
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <ToastContainer />
      <Typography variant="h4" gutterBottom color="#d32f2f">
        Approval Dashboard
      </Typography>

      <Grid container spacing={3}>
        {forApprovalTickets.map((ticket) => {
          return (
            <Grid item xs={12} key={ticket.ID}>
              <Card
                sx={{
                  borderLeft: "4px solid #d32f2f",
                  boxShadow: "0 2px 10px rgba(0,0,0,0.1)",
                }}
              >
                <CardContent>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      mb: 2,
                    }}
                  >
                    <Typography variant="h6">{ticket.Title}</Typography>
                    <Chip
                      label={ticket.Priority}
                      color={
                        ticket.Priority === "High"
                          ? "error"
                          : ticket.Priority === "Medium"
                          ? "warning"
                          : "success"
                      }
                    />
                  </Box>

                  <Typography variant="body2" color="text.secondary" paragraph>
                    {ticket.Description}
                  </Typography>

                  <Divider sx={{ my: 2 }} />

                  <Grid container spacing={2}>
                    <Grid item xs={12} md={4}>
                      <Stack spacing={1}>
                        <Typography variant="subtitle2">
                          <Person sx={{ verticalAlign: "middle", mr: 1 }} />
                          Assignee
                        </Typography>
                        <Box>
                          <Typography>{`${ticket?._Assignee?.FirstName} ${ticket?._Assignee?.LastName}`}</Typography>
                        </Box>
                      </Stack>
                    </Grid>

                    <Grid item xs={12} md={4}>
                      <Stack spacing={1}>
                        <Typography variant="subtitle2">
                          <CalendarToday
                            sx={{ verticalAlign: "middle", mr: 1 }}
                          />
                          Requested Timeline
                        </Typography>
                        <Typography>
                          {convertToRawDate(ticket.ExpectedStartDate)} to{" "}
                          {convertToRawDate(ticket.ExpectedEndDate)}
                        </Typography>
                        <Typography variant="caption">
                          Lead Time: {ticket.LeadTime}
                        </Typography>
                      </Stack>
                    </Grid>

                    <Grid item xs={12} md={4}>
                      <Stack spacing={1}>
                        <Typography variant="subtitle2">
                          <Notifications
                            sx={{ verticalAlign: "middle", mr: 1 }}
                          />
                          Approval Status
                        </Typography>
                        <Chip
                          label={ticket.TicketStatus}
                          color="primary"
                          variant="outlined"
                        />
                      </Stack>
                    </Grid>
                  </Grid>

                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "flex-end",
                      mt: 3,
                      gap: 2,
                    }}
                  >
                    <Button
                      variant="outlined"
                      startIcon={<Close />}
                      color="error"
                      onClick={() => handleReject(ticket)}
                    >
                      Reject
                    </Button>
                    <Button
                      variant="contained"
                      startIcon={<Check />}
                      color="success"
                      onClick={() => handleApprove(ticket)}
                    >
                      Approve
                    </Button>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          );
        })}
      </Grid>

      {/* Approval Dialog */}
      <Dialog
        open={approvalDialog.open}
        onClose={() => setApprovalDialog({ ...approvalDialog, open: false })}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>
          {approvalDialog.IsApproved ? "Approval" : "Rejection"}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ mt: 2 }}>
            {approvalDialog.IsApproved && (
              <>
                <TextField
                  label="Actual Start Date"
                  type="date"
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  value={approvalDialog.ActualStartDate}
                  onChange={(e) =>
                    setApprovalDialog({
                      ...approvalDialog,
                      ActualStartDate: e.target.value,
                    })
                  }
                />
                <TextField
                  label="Actual End Date"
                  type="date"
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  value={approvalDialog.ActualEndDate}
                  onChange={(e) =>
                    setApprovalDialog({
                      ...approvalDialog,
                      ActualEndDate: e.target.value,
                    })
                  }
                />
                <FormControl fullWidth>
                  <InputLabel>Updated Lead Time</InputLabel>
                  <Select
                    value={approvalDialog.LeadTime}
                    label="Updated Lead Time"
                    onChange={(e) =>
                      setApprovalDialog({
                        ...approvalDialog,
                        LeadTime: e.target.value,
                      })
                    }
                  >
                    {[
                      "1 week",
                      "2 weeks",
                      "3 weeks",
                      "1 month",
                      "2 months",
                    ].map((option) => (
                      <MenuItem key={option} value={option}>
                        {option}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </>
            )}

            <TextField
              label="Comment"
              multiline
              rows={3}
              fullWidth
              value={approvalDialog.Comment}
              onChange={(e) =>
                setApprovalDialog({
                  ...approvalDialog,
                  Comment: e.target.value,
                })
              }
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() =>
              setApprovalDialog({ ...approvalDialog, open: false })
            }
            color="error"
          >
            Cancel
          </Button>
          <Button
            onClick={confirmApproval}
            color={approvalDialog.IsApproved ? "success" : "error"}
            variant="contained"
            startIcon={<Check />}
          >
            Confirm {approvalDialog.IsApproved ? "Approval" : "Rejection"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ApprovalWorkflow;
